import { supabase } from "../lib/supabaseClient";
import type { JobRecord } from "../hooks/useJobCenter";
import { JobKinds, type JobKind, type JobPayloadFor } from "../supabase/functions/_shared/jobKinds";
import { buildRetryEnqueueRequest, RETRY_SOURCE_COLUMNS, type RetrySourceRow } from "../lib/jobs/jobRetry";

// Generic, strongly typed enqueue payload
export interface EnqueueJobPayload<K extends JobKind = JobKind> {
  type: K;
  label: string;
  payload: JobPayloadFor<K>;
  total?: number; // optional overall item count for progress bars
  dedupe_key?: string; // optional key to reuse an existing identical job
}

/** Erreur d'enqueue ; `code` = code renvoyé par enqueue-job (ex. 'JOB_FORBIDDEN'). */
export class JobEnqueueError extends Error {
  readonly code: string | null;
  readonly status: number | null;
  constructor(message: string, code: string | null = null, status: number | null = null) {
    super(message);
    this.name = 'JobEnqueueError';
    this.code = code;
    this.status = status;
  }
}

/**
 * enqueue-job renvoie `{ error, code }` ; supabase-js n'expose que « Edge
 * Function returned a non-2xx status code » et garde la réponse dans
 * `error.context`. Pour un refus de droits (code JOB_*), le message français
 * du serveur est montré tel quel dans le toast de l'appelant ; sinon on garde
 * le message historique.
 */
export async function toEnqueueError(error: any): Promise<JobEnqueueError> {
  const fallback = `Could not start the job: ${error?.message ?? String(error)}`;
  const ctx = error?.context;
  const status: number | null = typeof ctx?.status === 'number' ? ctx.status : null;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = await (typeof ctx.clone === 'function' ? ctx.clone() : ctx).json();
      const code = typeof body?.code === 'string' ? body.code : null;
      if (code && code.startsWith('JOB_') && typeof body?.error === 'string' && body.error.trim()) {
        return new JobEnqueueError(body.error, code, status);
      }
    } catch {
      // Corps absent ou non JSON : message historique.
    }
  }
  return new JobEnqueueError(fallback, null, status);
}

export interface EnqueueJobOptions {
  /**
   * En cas d'échec, retirer la ligne provisoire au lieu de la passer en
   * « failed ». Utilisé par la relance : l'échec est annoncé par un toast et la
   * tâche d'origine reste affichée, une seconde ligne en échec ferait doublon
   * (et sa suppression viserait un id « tmp_… » inconnu de la base).
   */
  dropPlaceholderOnError?: boolean;
}

export interface RetryJobResult {
  /** Nouvelle tâche (ou tâche identique déjà en cours, réutilisée par dedupe_key). */
  job: JobRecord;
  /** La ligne d'origine a été supprimée : la nouvelle la remplace dans le Job Center. */
  previousRemoved: boolean;
}

export const jobService = {
  /**
   * Enqueues a new job to be processed by the backend.
   * This is the primary method for starting any background task.
   */
  async enqueueJob<K extends JobKind>(
    jobDetails: EnqueueJobPayload<K>,
    options: EnqueueJobOptions = {},
  ): Promise<JobRecord> {
    // 1. Create a deterministic temporary client ID (will be replaced after server response).
    // We keep same ID once server responds, so we wait to extract id from server response.
    // But we still want an immediate placeholder row BEFORE network latency; to do that,
    // we dispatch a pseudo optimistic job with a temporary negative timestamp-based id,
    // then reconcile once real id arrives.
    const placeholderId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    if (typeof window !== 'undefined') {
      try {
        const optimisticRecord: Partial<JobRecord> & { id: string } = {
          id: placeholderId,
          type: jobDetails.type,
          label: jobDetails.label,
          scope: (jobDetails.payload as any)?.scope,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          status: 'pending' as any,
          total: jobDetails.total,
          completed: 0,
          artifactUrl: undefined,
          error: undefined,
          meta: jobDetails.payload || {},
        };
        (optimisticRecord as any)._isPlaceholder = true;
        window.dispatchEvent(new CustomEvent('job:enqueue', { detail: optimisticRecord }));
      } catch (e) {
        console.warn('[jobService] Placeholder optimistic dispatch failed', e);
      }
    }

    const { data, error } = await supabase.functions.invoke('enqueue-job', {
      body: jobDetails
    });

    if (error) {
      console.error("Failed to enqueue job:", error);
      const enqueueError = await toEnqueueError(error);
      // Emit a failure state for the placeholder so user sees immediate feedback
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('job:enqueue:fail', {
          detail: { id: placeholderId, error: enqueueError.message, drop: options.dropPlaceholderOnError === true },
        }));
      }
      throw enqueueError;
    }

    const job = data.job;
    // 2. Reconcile: replace placeholder with real job id & data (or just add if placeholder missing)
    if (typeof window !== 'undefined' && job) {
      try {
        const optimisticRecord: Partial<JobRecord> & { id: string } = {
          id: job.id,
          type: job.type,
          label: job.label || jobDetails.label,
          userId: (job as any).user_id ?? null,
          scope: job.payload?.scope,
          createdAt: Date.now(), // client time; realtime update will refine
          updatedAt: Date.now(),
          status: job.status || 'pending',
          total: job.total || jobDetails.total || undefined,
          completed: job.progress || 0,
          artifactUrl: job.result?.artifactUrl,
          error: job.error_message,
          meta: job.payload || jobDetails.payload || {},
        };
        window.dispatchEvent(new CustomEvent('job:enqueue:reconcile', { detail: { placeholderId, record: optimisticRecord } }));
      } catch (e) {
        console.warn('[jobService] Failed to dispatch reconcile event', e);
      }
    }
    return job as JobRecord;
  },

  /**
   * « Relancer » : relit la tâche d'origine (la sienne, en échec ou annulée,
   * d'un type relançable), en crée une NOUVELLE par enqueue-job — autorisée
   * par can_enqueue_job avec le JWT de l'utilisateur, lancée par le webhook
   * INSERT — puis supprime l'ancienne ligne pour que le Job Center (et le
   * compteur « Relancer tout ») ne la propose plus. Une suppression refusée
   * n'annule pas la relance : la nouvelle tâche est déjà partie.
   */
  async retryJob(jobId: string, userId: string): Promise<RetryJobResult> {
    const { data: row, error: readError } = await supabase
      .from('jobs')
      .select(RETRY_SOURCE_COLUMNS)
      .eq('id', jobId)
      .eq('user_id', userId)
      .maybeSingle();
    if (readError) throw readError;

    const request = buildRetryEnqueueRequest(row as RetrySourceRow | null, userId);
    const job = await jobService.enqueueJob(request, { dropPlaceholderOnError: true });

    let previousRemoved = false;
    if (job?.id && job.id !== jobId) {
      const { error: deleteError } = await supabase
        .from('jobs')
        .delete()
        .match({ id: jobId, user_id: userId });
      if (deleteError) {
        console.warn('[jobService] Retried job kept: delete refused', deleteError.message);
      } else {
        previousRemoved = true;
      }
    }
    return { job, previousRemoved };
  },

  async cancelJob(jobId: string): Promise<void> {
    const { error } = await supabase.functions.invoke('cancel-job', { body: { jobId } });
    if (error) throw new Error(`Could not stop the job: ${error.message}`);
  },
};
